import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMutation } from '@apollo/client';

import { addDays } from 'date-fns';
import { InvoiceType } from 'graphql-query-contracts';
import { deleteDraftChargeMutation } from 'lane-shared/domains/billingPayments/queries';
import { Charge } from 'lane-shared/domains/billingPayments/types';

import {
  CreateChargeRequestParams,
  createChargeRequestFromDraft,
} from './createChargeRequestFromDraft';
import { pendingChargeFromDraft } from './pendingChargeFromDraft';
import {
  CompletedSubmission,
  useVerifyAndSubmitCharge,
} from './useVerifyAndSubmitCharge';

type Params = {
  draftCharge: Charge;
  chargeCount: number;
  request: Omit<CreateChargeRequestParams, 'draft' | 'invoiceDueDate'> & {
    invoiceDueInDays: number;
  };
  onSubmitCharge?: (charge: Charge) => void;
};

type SubmitInput = {
  invoiceDueDate?: Date;
};

export function useVerifyAndSubmitDraft({
  draftCharge,
  chargeCount,
  request,
  onSubmitCharge,
}: Params) {
  const { t } = useTranslation();
  const { invoiceDueInDays, ...chargeRequestParams } = request;
  const { currency } = chargeRequestParams;
  const verifyAndSubmitCharge = useVerifyAndSubmitCharge<Charge>(
    request.externalEntityId
  );
  const [deleteDraft] = useMutation(deleteDraftChargeMutation);
  const [submittedCharge, setSubmittedCharge] = useState<Charge>();
  // A ref so a second confirm during the success sequence cannot read stale state.
  const hasSubmitted = useRef(false);

  useEffect(() => {
    hasSubmitted.current = false;
    setSubmittedCharge(undefined);
  }, [request.externalEntityId]);

  // Retry runs after the user may have edited the draft, so the stub and toast use what was sent.
  const completeSubmission = async ({
    payload,
    request: sentRequest,
    context: sentDraft,
  }: CompletedSubmission<Charge>) => {
    hasSubmitted.current = true;

    const stub = pendingChargeFromDraft({
      draft: sentDraft,
      chargeId: payload.chargeId,
      name: sentRequest.name,
      amount: sentRequest.amount,
      currency,
      invoiceType: sentRequest.invoiceType,
      createdAt: new Date(),
    });

    setSubmittedCharge(stub);

    try {
      await deleteDraft({
        variables: { deleteDraftChargeRequest: { id: draftCharge.id } },
      });
    } catch {
      window.Toast.show(
        t('abp.charges.chargeManager.draftCharge.deleteDraftChargeError')
      );
    }

    // Toast.show resolves only once the toast hides, so it must not be awaited.
    window.Toast.show(
      sentRequest.invoiceType === InvoiceType.InvoiceTypeMonthly
        ? t('abp.charges.verifyAndSubmit.submitToast.monthly')
        : t('abp.charges.verifyAndSubmit.submitToast')
    );
    onSubmitCharge?.(stub);
  };

  const submit = async ({ invoiceDueDate }: SubmitInput) => {
    if (hasSubmitted.current) {
      return;
    }

    const createChargeRequest = createChargeRequestFromDraft({
      ...chargeRequestParams,
      draft: draftCharge,
      chargeName: `${request.chargeName}-C${chargeCount + 1}`,
      invoiceDueDate: invoiceDueDate ?? addDays(new Date(), invoiceDueInDays),
    });
    const completed = await verifyAndSubmitCharge.submit(
      createChargeRequest,
      draftCharge
    );

    if (completed) {
      await completeSubmission(completed);
    }
  };

  const retry = async () => {
    const completed = await verifyAndSubmitCharge.retry();

    if (completed) {
      await completeSubmission(completed);
    }
  };

  return {
    submit,
    retry,
    submittedCharge,
    isSubmitting: verifyAndSubmitCharge.isSubmitting,
    failure: verifyAndSubmitCharge.failure,
  };
}
